import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Sector,
  Legend,
} from 'recharts';
import { dashboardItemsSelector } from '../../redux/dashboard';
import { CircularProgress, Typography } from '@mui/material';
import ApiConfig from '../../config/ApiConfig';

const chartDataFormat = [
  { name: 'Pending', value: 0, dataKey: 'pending', color: '#FFBB28' },
  { name: 'Confirmed', value: 0, dataKey: 'confirmed', color: '#10b55d' },
  { name: 'Rejected', value: 0, dataKey: 'rejected', color: '#db1212' },
];

const RADIAN = Math.PI / 180;

const OrderStatus = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const onPieEnter = useCallback((_, index) => {
    setActiveIndex(index);
  }, []);

  const defaultChartData = useSelector(dashboardItemsSelector);
  const chartData = defaultChartData?.chartData?.orderStatusAnalytics?.data;
  const isLoading =
    defaultChartData?.chartData?.orderStatusAnalytics?.isLoading;

  const data = chartDataFormat.map((item) => ({
    ...item,
    value: chartData?.[item.dataKey] ?? 0,
  }));

  const renderActiveShape = (props: any) => {
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      value,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);

    return (
      <g>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
          {`${payload.name}`}
        </text>
        <text x={cx} y={cy} dy={28} textAnchor="middle" fill={fill}>
          {`${value}`}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
      </g>
    );
  };

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    value,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x > cx ? x - 10 : x + 20}
        y={y}
        fontSize={10}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
      >
        {`${value}`}
      </text>
    );
  };

  const renderLegend = ({ payload }) => {
    const tabMap = {
      Pending: 2,
      Confirmed: 3,
      Rejected: 4,
    };

    return (
      <ul
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          listStyle: 'none',
          padding: 0,
          margin: 0,
          gap: '12px',
        }}
      >
        {payload.map((entry, index) => (
          <li
            key={`item-${index}`}
            onClick={() => {
              setActiveIndex(index);
              const tab = tabMap[entry.payload.name];
              if (tab) {
                window.location.href = `${ApiConfig.REACT_APP_GRAPH_REDIRECT}/order?tab=${tab}`;
              }
            }}
            style={{
              cursor: 'pointer',
              color: index === activeIndex ? entry.color : '#333',
              fontWeight: index === activeIndex ? 'bold' : 'normal',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg width="14" height="14" style={{ marginRight: 6 }}>
              <rect width="14" height="14" fill={entry.color} />
            </svg>
            <span>{entry.value}</span>
          </li>
        ))}
      </ul>
    );
  };
  return (
    <>
      <Typography variant="h3" className="mediumFont" style={{ marginTop: 20 }}>
        Order Status
      </Typography>

      <ResponsiveContainer width="100%" height={400}>
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <PieChart>
            <Pie
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="50%"
              outerRadius="80%"
              minAngle={20}
              dataKey="value"
              labelLine={false}
              onMouseEnter={onPieEnter}
              label={renderCustomizedLabel}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Legend content={renderLegend} />
          </PieChart>
        )}
      </ResponsiveContainer>
    </>
  );
};

export default OrderStatus;
